import {
    queueFeedCardClicked,
    trackFeedPageShown,
    type AnalyticsFeedOrdering,
    type AnalyticsFeedType,
} from "@/main/analytics/analyticsEvents";
import { recordFeedClickOrigin } from "@/main/analytics/feedClickOrigin";
import { ClientFactory } from "@/main/common/ClientFactory";

/**
 * The one place a feed card click turns into measurement.
 *
 * <p>Two things have to happen on that click and they are useless apart: the GA4 event (rich
 * dimensions, but blind to the 38,957 anonymous views in a 30-day window, to consent-decliners and to
 * ad-blockers) and the click-origin handoff that becomes `discussion_views.source` (one low-cardinality
 * token, but it covers everybody and can be joined against product data in SQL). Routing both through
 * `onFeedCardActivated` means no call site can wire up half of it. Design:
 * designs/01-now/2026-07-30-feed-discovery-instrumentation-design.md.
 */

/**
 * What a feed knows about itself, threaded down to each card. `position` is filled in per card by
 * `DiscussionCardDisplayer`'s caller.
 */
export type FeedCardAttribution = {
    feedType: AnalyticsFeedType;
    ordering: AnalyticsFeedOrdering;
    /** 1-based slot within the loaded list. */
    position: number;
    /** Straight off the DTO for the home feed; absent everywhere else. */
    recommendationSource?: string | null;
    /**
     * The card's RAW primary source (`sourceType` on the DTO), before the `MULTIPLE` label overwrote it
     * (feed-ranking measurement design §4 M2.3). Home feed only; `DiscussionCardDisplayer` fills it from the
     * card's own DTO, so no list has to thread it.
     */
    primarySource?: string | null;
};

/**
 * `feed_type` → the `discussion_views.source` token. The server validates against its own copy of this
 * closed set (`DiscussionViewSource`) and coerces anything else to `unknown`, so this map is a
 * convenience, never a trust boundary.
 *
 * <p>`groups` (the home page's "Your groups" tab) and `group` (a single group's page) map to DIFFERENT
 * tokens on purpose — collapsing them makes "are the Groups feed's weekly users engaged or accidental"
 * unanswerable, which is one of the questions the column exists to answer.
 */
const FEED_TYPE_TO_VIEW_SOURCE: Record<AnalyticsFeedType, string | null> = {
    home: "home_feed",
    all: "all_feed",
    groups: "home_groups_feed",
    unanswered: "unanswered_feed",
    /**
     * The home page's "Boards to vote on" lane (board pace design §10, S3). The token must stay
     * byte-identical to `DiscussionViewSource.BOARDS_FEED.toStoredValue()`: the two taxonomies have no
     * compile-time link, and a disagreement fails no build — it writes `unknown` rows, which that enum
     * reserves as a **defect signal, never a traffic category**.
     *
     * <p>This entry was `null` between the lane shipping and 2026-09-11, so the lane recorded nothing.
     * Pinned from both sides now: `feedAttribution.spec.ts` asserts the string this file emits, and
     * `DiscussionViewServiceTests` asserts the string the server accepts.
     */
    boards: "boards_feed",
    /**
     * The two audience scopes (HS-2b). Byte-identical to `DiscussionViewSource.HOME_FOLLOWING_FEED` /
     * `HOME_FOLLOWED_SERIES_FEED.toStoredValue()`, pinned from both sides like `boards` above
     * (`feedAttribution.spec.ts` here, `DiscussionViewServiceTests` there).
     */
    following: "home_following_feed",
    "series-followed": "home_followed_series_feed",
    group: "group_feed",
    series: "series_feed",
    user: "user_feed",
    search: "search",
    bookmarks: "bookmarks_feed",
};

/**
 * `source_detail` carries the recommendation source, and NOTHING when there is none (SV-121 M2.2,
 * feed-ranking measurement design §4 M2 item 2). It used to fall back to the ordering token, which
 * silently mixed two taxonomies in one `varchar(32)`: a missing recommendation source stored as "new"
 * was indistinguishable afterwards from a genuine ordering-sourced view. An absent source is data; a
 * wrong source is a lie. The ordering still reaches GA4 through `queueFeedCardClicked`.
 */
function resolveSourceDetail(attribution: FeedCardAttribution): string | undefined {
    const recommendationSource = attribution.recommendationSource?.trim();
    return recommendationSource ? recommendationSource : undefined;
}

/**
 * The shape the server accepts for a source token (`FeedClickRequest`'s `@Pattern`). A token outside it would
 * 400 the whole click, so it is dropped here instead and the click still records with that source absent.
 */
const SOURCE_TOKEN = /^[A-Z][A-Z0-9_]{0,31}$/;

function sourceToken(value: string | null | undefined): string | undefined {
    const trimmed = value?.trim();
    return trimmed && SOURCE_TOKEN.test(trimmed) ? trimmed : undefined;
}

/**
 * The `feed_click` half (SV-121 M2.1 + M2.3, round 33 slice 3a): one row per card opened from the ranked HOME
 * feed, and from no other surface (decision A11: `feed_impression`, the denominator it joins, records the home
 * feed only). Carries the 1-based list slot, the raw primary source and the displayed label. Fire-and-forget:
 * `ClientFactory.recordFeedClick` resolves on every failure and is never awaited here.
 */
function recordHomeFeedClick(attribution: FeedCardAttribution, discussionPublicId: string): void {
    if (attribution.feedType !== "home") {
        return;
    }
    const primarySource = sourceToken(attribution.primarySource);
    const recommendationSource = sourceToken(attribution.recommendationSource);
    void ClientFactory.recordFeedClick({
        discussionPublicId,
        slot: attribution.position,
        ...(primarySource ? { primarySource } : {}),
        ...(recommendationSource ? { recommendationSource } : {}),
    });
}

/**
 * Call from the card's own click/activation handler — never from a `useEffect`, and never from a
 * container's `onClickCapture` that would also fire for a vote or a bookmark toggle inside the card.
 *
 * <p>Analytics is strictly side-effect-only: nothing here may throw into the navigation it rides on.
 */
export function onFeedCardActivated(
    attribution: FeedCardAttribution,
    discussionPublicId: string | undefined | null,
): void {
    try {
        queueFeedCardClicked({
            feedType: attribution.feedType,
            ordering: attribution.ordering,
            position: attribution.position,
            recommendationSource: attribution.recommendationSource,
        });

        const viewSource = FEED_TYPE_TO_VIEW_SOURCE[attribution.feedType];
        // A surface with no label records NOTHING rather than an empty or invented one. `null` on the
        // column means "unattributed", which is the honest state for a surface nobody has wired up.
        // Every entry in the map above carries a token today, so this guard is a fence for the next
        // `feed_type` added, not a description of any live surface.
        if (discussionPublicId && viewSource) {
            recordFeedClickOrigin({
                discussionPublicId,
                source: viewSource,
                sourceDetail: resolveSourceDetail(attribution),
            });
        }
    } catch {
        // deliberately swallowed — a measurement failure must never cost the user their click
    }
    // Its own guard, so a failure in either half above never costs the click record, and vice versa.
    try {
        if (discussionPublicId) {
            recordHomeFeedClick(attribution, discussionPublicId);
        }
    } catch {
        // deliberately swallowed — a measurement failure must never cost the user their click
    }
}

/**
 * The `discussion_views.source` token for a view opened from the empty-room hand-off card (FI-139).
 * Byte-identical to `DiscussionViewSource.HANDOFF.toStoredValue()`, pinned from both sides:
 * `feedAttribution.spec.ts` here, `DiscussionViewServiceTests` there.
 */
export const HANDOFF_VIEW_SOURCE = "handoff";

/**
 * Call from the hand-off card CTA's click handler. The card is not a feed, so there is no GA4 half
 * (its impression and click are control-census entries, empty-room design §7); only the click-origin
 * slot that `DiscussionViewTracker` turns into `discussion_views.source`. Side-effect-only, never throws.
 */
export function onHandoffCardActivated(discussionPublicId: string | undefined | null): void {
    try {
        if (discussionPublicId) {
            recordFeedClickOrigin({ discussionPublicId, source: HANDOFF_VIEW_SOURCE });
        }
    } catch {
        // deliberately swallowed — a measurement failure must never cost the user their click
    }
}

/** How recent a post has to be to count toward `fresh_item_count` (the fresh-slot rotation oracle). */
const FRESH_ITEM_WINDOW_MS = 72 * 60 * 60 * 1000;

export function countFreshItems(items: ReadonlyArray<{ createdAt?: string | null }>): number {
    const cutoff = Date.now() - FRESH_ITEM_WINDOW_MS;

    return items.reduce((count, item) => {
        const createdAt = item?.createdAt;
        if (!createdAt) {
            return count;
        }

        const publishedAtMs = Date.parse(createdAt);
        return Number.isFinite(publishedAtMs) && publishedAtMs >= cutoff ? count + 1 : count;
    }, 0);
}

/**
 * The `feed_page_shown` half. Callers must already have deduped — this fires whenever it is called.
 * `PaginatedListWrapperV2.onPageShown` is the fire-once seam for every list built on it.
 */
export function reportFeedPageShown(options: {
    feedType: AnalyticsFeedType;
    ordering: AnalyticsFeedOrdering;
    pageIndex: number;
    items: ReadonlyArray<{ createdAt?: string | null }>;
}): void {
    try {
        trackFeedPageShown({
            feedType: options.feedType,
            ordering: options.ordering,
            pageIndex: options.pageIndex,
            itemCount: options.items.length,
            freshItemCount: countFreshItems(options.items),
        });
    } catch {
        // side-effect-only
    }
}
